"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useFeatureFlags } from "./FeatureFlagsProvider";
export type ColorTheme = "warm" | "classic";
export type ThemeMode = "classic" | "warm" | "selector";
const STORAGE_KEY = "trackshare-color-theme";
const THEME_CLASSES: Record<ColorTheme, string | null> = {
  warm: null,
  // default — no class needed
  classic: "theme-classic"
};
interface ColorThemeContextValue {
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
  showSelector: boolean;
}
const ColorThemeContext = createContext<ColorThemeContextValue | undefined>(undefined);
function getStoredTheme(): ColorTheme {
  if (typeof window === "undefined") return "warm";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "classic") return "classic";
  return "warm";
}
function applyThemeClass(theme: ColorTheme) {
  const html = document.documentElement;
  // Remove all theme classes
  Object.values(THEME_CLASSES).forEach(cls => {
    if (cls) html.classList.remove(cls);
  });
  // Apply the new one (if any)
  const cls = THEME_CLASSES[theme];
  if (cls) html.classList.add(cls);
}
export function ColorThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    themeMode: rawThemeMode
  } = useFeatureFlags();
  const themeMode = rawThemeMode === "warm" || rawThemeMode === "classic" || rawThemeMode === "selector" ? rawThemeMode as ThemeMode : "classic";
  const showSelector = themeMode === "selector";

  // Track user-selected theme (only meaningful in selector mode)
  // Initialize with the correct theme based on initial themeMode
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => themeMode === "selector" ? getStoredTheme() : themeMode);

  // Sync state when themeMode changes (feature flag update)
  // Uses the "derive state from props" pattern with previous value comparison
  const [prevThemeMode, setPrevThemeMode] = useState(themeMode);
  if (themeMode !== prevThemeMode) {
    setPrevThemeMode(themeMode);
    const newTheme = themeMode === "selector" ? getStoredTheme() : themeMode;
    setColorThemeState(newTheme);
  }

  // Apply the theme class to the DOM when theme changes
  useEffect(() => {
    applyThemeClass(colorTheme);
  }, [colorTheme]);

  // Clear stale localStorage when not in selector mode — prevents a previously
  // stored theme from bleeding through if the flag transitions away from "selector"
  useEffect(() => {
    if (!showSelector) localStorage.removeItem(STORAGE_KEY);
  }, [showSelector]);
  const setColorTheme = useCallback((theme: ColorTheme) => {
    setColorThemeState(theme);
    // Only persist the selection when the user is actively choosing — selector mode only
    if (showSelector) localStorage.setItem(STORAGE_KEY, theme);
    applyThemeClass(theme);
  }, [showSelector]);
  return <ColorThemeContext.Provider value={{
    colorTheme,
    setColorTheme,
    showSelector
  }} data-sentry-element="ColorThemeContext.Provider" data-sentry-component="ColorThemeProvider" data-sentry-source-file="color-theme-provider.tsx">
      {children}
    </ColorThemeContext.Provider>;
}
export function useColorTheme() {
  const context = useContext(ColorThemeContext);
  if (!context) {
    throw new Error("useColorTheme must be used within a ColorThemeProvider");
  }
  return context;
}